import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Divider,
    Checkbox,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Snackbar,
    Alert,
    Link,
    IconButton,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Input } from 'components';
import TermsAndConditions from '../TermsAndConditions/TermsAndConditons';
import data from '../TermsAndConditions/data.json';
import ETLogo from '../../../../assets/environment-testing.png';
import {
    CONTACT_NUMBER_FIELD_MAX_LENGTH,
    FIFTY_FIELD_MAX_LENGTH,
    LONG_TEXT_FIELD_MAX_LENGTH,
    POSTCODE_FIELD_MAX_LENGTH,
    TEXT_FIELD_MAX_LENGTH
} from './validationLimits';

const DECLARATION_COMPANY_LENGTH_ERROR = 'Company cannot exceed 200 characters.';
const DECLARATION_TITLE_LENGTH_ERROR = 'Title cannot exceed 100 characters.';
const SIGNATURE_ID_LENGTH_ERROR = 'Signature ID cannot exceed 200 characters.';
const GENERAL_COMPANY_NAME_LENGTH_ERROR = 'Company Name cannot exceed 100 characters.';
const GENERAL_TRADING_NAME_LENGTH_ERROR = 'Trading Name cannot exceed 100 characters.';
const GENERAL_BUSINESS_STREET_LENGTH_ERROR = 'Business Street Name cannot exceed 100 characters.';
const GENERAL_BUSINESS_SUBURB_LENGTH_ERROR = 'Business Suburb cannot exceed 100 characters.';
const GENERAL_BUSINESS_POSTCODE_LENGTH_ERROR = 'Business PostCode cannot exceed 6 characters.';
const GENERAL_POSTAL_STREET_LENGTH_ERROR = 'Postal Street Name cannot exceed 100 characters.';
const GENERAL_POSTAL_SUBURB_LENGTH_ERROR = 'Postal Suburb cannot exceed 100 characters.';
const GENERAL_POSTAL_POSTCODE_LENGTH_ERROR = 'Postal Code cannot exceed 6 characters.';
const GENERAL_EMAIL_STATEMENTS_LENGTH_ERROR = 'Email for statements cannot exceed 50 characters.';
const GENERAL_EMAIL_INVOICES_LENGTH_ERROR = 'Email for Invoices cannot exceed 50 characters.';
const GENERAL_PO_EMAIL_LENGTH_ERROR = 'PO Email cannot exceed 50 characters.';
const GENERAL_PHONE_NUMBER_LENGTH_ERROR = 'Phone number cannot exceed 10 digits.';
const GENERAL_MOBILE_NUMBER_LENGTH_ERROR = 'Mobile number cannot exceed 10 digits.';
const FULL_NAME_LENGTH_ERROR = 'Full Name cannot exceed 100 characters.';
const COMPANY_LENGTH_ERROR = 'Company cannot exceed 100 characters.';
const POSITION_LENGTH_ERROR = 'Position Title cannot exceed 100 characters.';
const EUROFINS_CONTACT_LENGTH_ERROR = 'Eurofins Contact Name cannot exceed 200 characters';

interface Props {
    formData: any;
    australianStates?: any[];
    onFormDataChange?: (data: Partial<any>) => void;
    onBack: () => void;
    onBackToGeneralInfo?: () => void;
    onSubmit: (declarationData: any) => void;
    isSubmitting?: boolean;
    onSave?: (declarationData: any) => void;
    isSaving?: boolean;
    isDisabled?: boolean;
    onGoToStep?: (step: number, errors?: Record<string, string>) => void;
    getSignatureFromParent: any;
}

export const Confirmation: React.FC<Props> = ({
    formData,
    australianStates = [],
    onFormDataChange,
    onBack,
    onBackToGeneralInfo,
    onSubmit,
    isSubmitting = false,
    onSave,
    isDisabled = false,
    isSaving = false,
    onGoToStep,
    getSignatureFromParent
}) => {
    const initialDeclaration = formData?.declaration || {};
    const [hasAgreedToTermsAndCondition, setHasAgreedToTermsAndCondition] = useState(initialDeclaration.hasAgreedToTermsAndCondition || false);
    const [declarationFormData, setDeclarationFormData] = useState({
        fullName: '',
        date: '',
        company: '',
        title: '',
        tandCComments: ''
    });
    const [signatureImage, setSignatureImage] = useState<File | null>(null);
    const [hasClickedTermsLink, setHasClickedTermsLink] = useState(formData.requestStatus === 5 ? true : (initialDeclaration.hasClickedTermsLink || false));
    const [showTermsDialog, setShowTermsDialog] = useState(false);
    const [canApproveTerms, setCanApproveTerms] = useState(false);
    const [showCheckbox, setShowCheckbox] = useState(initialDeclaration.hasAgreedToTermsAndCondition || false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'error' | 'warning' }>({
        open: false,
        message: '',
        severity: 'error'
    });

    const signaturePadRef = useRef<SignatureCanvas>(null);

    const generalInfo = formData || {};
    const creditData = formData?.creditAccountData || {};
    const businessAddress = generalInfo?.businessAddress || {};
    const postalAddress = generalInfo?.postalAddress || {};

    useEffect(() => {
        setDeclarationFormData({
            fullName: initialDeclaration.fullName || '',
            date: initialDeclaration.date || initialDeclaration.requestedDate || new Date().toISOString().split('T')[0],
            company: initialDeclaration.company || '',
            title: initialDeclaration.title || '',
            tandCComments: initialDeclaration.tandCComments || ''
        });
    }, [initialDeclaration]);

    const getAustralianStateLabel = (country: string, value: any) => {
        if (!value) {
            return '';
        }

        if (country !== 'Australia') {
            return value;
        }

        const matchedState = australianStates.find((state: any) => (
            String(state?.id) === String(value)
            || state?.name === value
            || state?.displayName === value
            || state?.value === value
        ));

        return matchedState?.name || matchedState?.displayName || matchedState?.value || value;
    };

    const skippedCreditAccountDetails = formData?.invoiceStatement?.needCreditAccount === 'no';

    const validateGeneralInfo = () => {
        const newErrors: Record<string, string> = {};

        if (!generalInfo.companyName?.trim()) {
            newErrors.companyName = 'Company/Business Name is required';
        } else if (generalInfo.companyName.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors.companyName = GENERAL_COMPANY_NAME_LENGTH_ERROR;
        }
        if (!generalInfo.abn?.trim() && generalInfo.invoiceStatement?.needCreditAccount !== 'no') {
            newErrors.abn = 'ABN is required';
        }
        if (!generalInfo.tradingName?.trim()) {
            newErrors.tradingName = 'Trading Name is required';
        } else if (generalInfo.tradingName.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors.tradingName = GENERAL_TRADING_NAME_LENGTH_ERROR;
        }
        if (!generalInfo.contactName?.trim()) {
            newErrors.contactName = 'Contact Name is required';
        }
        if (!generalInfo.eurofinsContactName?.trim()) {
            newErrors.eurofinsContactName = 'Eurofins Contact Name is required';
        } else if (generalInfo.eurofinsContactName.trim().length > LONG_TEXT_FIELD_MAX_LENGTH) {
            newErrors.eurofinsContactName = EUROFINS_CONTACT_LENGTH_ERROR;
        }
        if (!generalInfo.emailAddress?.trim()) {
            newErrors.emailAddress = 'Email Address is required';
        }
        if (!generalInfo.contactNumberType) {
            newErrors.contactNumberType = 'Contact number type is required';
        } else if (generalInfo.contactNumberType === 'phoneNumber' && !generalInfo.phoneNumber?.trim()) {
            newErrors.contactNumber = 'Phone Number is required';
        } else if (generalInfo.contactNumberType === 'phoneNumber' && generalInfo.phoneNumber.trim().length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
            newErrors.contactNumber = GENERAL_PHONE_NUMBER_LENGTH_ERROR;
        } else if (generalInfo.contactNumberType === 'mobileNumber' && !generalInfo.mobileNumber?.trim()) {
            newErrors.contactNumber = 'Mobile Number is required';
        } else if (generalInfo.contactNumberType === 'mobileNumber' && generalInfo.mobileNumber.trim().length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
            newErrors.contactNumber = GENERAL_MOBILE_NUMBER_LENGTH_ERROR;
        }
        if (!businessAddress.street?.trim()) {
            newErrors['businessAddress.street'] = 'Business Street is required';
        } else if (businessAddress.street.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors['businessAddress.street'] = GENERAL_BUSINESS_STREET_LENGTH_ERROR;
        }
        if (!businessAddress.suburb?.trim()) {
            newErrors['businessAddress.suburb'] = 'Business Suburb is required';
        } else if (businessAddress.suburb.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors['businessAddress.suburb'] = GENERAL_BUSINESS_SUBURB_LENGTH_ERROR;
        }
        if (!businessAddress.postcode?.trim()) {
            newErrors['businessAddress.postcode'] = 'Business Post Code is required';
        } else if (businessAddress.postcode.trim().length > POSTCODE_FIELD_MAX_LENGTH) {
            newErrors['businessAddress.postcode'] = GENERAL_BUSINESS_POSTCODE_LENGTH_ERROR;
        }
        if (!businessAddress.state) {
            newErrors['businessAddress.state'] = 'Business State is required';
        }

        if (!generalInfo.isPostalAddressSameAsBusinessAddress) {
            if (!postalAddress.street?.trim()) {
                newErrors['postalAddress.street'] = 'Postal Street is required';
            } else if (postalAddress.street.trim().length > TEXT_FIELD_MAX_LENGTH) {
                newErrors['postalAddress.street'] = GENERAL_POSTAL_STREET_LENGTH_ERROR;
            }
            if (!postalAddress.suburb?.trim()) {
                newErrors['postalAddress.suburb'] = 'Postal Suburb is required';
            } else if (postalAddress.suburb.trim().length > TEXT_FIELD_MAX_LENGTH) {
                newErrors['postalAddress.suburb'] = GENERAL_POSTAL_SUBURB_LENGTH_ERROR;
            }
            if (!postalAddress.postcode?.trim()) {
                newErrors['postalAddress.postcode'] = 'Postal Post Code is required';
            } else if (postalAddress.postcode.trim().length > POSTCODE_FIELD_MAX_LENGTH) {
                newErrors['postalAddress.postcode'] = GENERAL_POSTAL_POSTCODE_LENGTH_ERROR;
            }
            if (!postalAddress.state) {
                newErrors['postalAddress.state'] = 'Postal State is required';
            }
        }

        const invoiceStatement = generalInfo.invoiceStatement || {};
        if (!invoiceStatement.contactName?.trim()) {
            newErrors['invoiceStatement.contactName'] = 'Contact Name for Invoices is required';
        }
        if (invoiceStatement.phoneNumber?.trim() && invoiceStatement.phoneNumber.trim().length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
            newErrors['invoiceStatement.phoneNumber'] = GENERAL_PHONE_NUMBER_LENGTH_ERROR;
        }
        if (!invoiceStatement.emailAddressStatements?.trim()) {
            newErrors['invoiceStatement.emailAddressStatements'] = 'Email Address for Statements is required';
        } else if (invoiceStatement.emailAddressStatements.trim().length > FIFTY_FIELD_MAX_LENGTH) {
            newErrors['invoiceStatement.emailAddressStatements'] = GENERAL_EMAIL_STATEMENTS_LENGTH_ERROR;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceStatement.emailAddressStatements.trim())) {
            newErrors['invoiceStatement.emailAddressStatements'] = 'Please enter a valid email address';
        }

        if (!invoiceStatement.emailAddressInvoices?.trim()) {
            newErrors['invoiceStatement.emailAddressInvoices'] = 'Email Address for Invoices is required';
        } else if (invoiceStatement.emailAddressInvoices.trim().length > FIFTY_FIELD_MAX_LENGTH) {
            newErrors['invoiceStatement.emailAddressInvoices'] = GENERAL_EMAIL_INVOICES_LENGTH_ERROR;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceStatement.emailAddressInvoices.trim())) {
            newErrors['invoiceStatement.emailAddressInvoices'] = 'Please enter a valid email address';
        }

        if (!invoiceStatement.isPONumberMandatory) {
            newErrors['invoiceStatement.isPONumberMandatory'] = 'Please select an option';
        }

        if (invoiceStatement.isPONumberMandatory === 'yes') {
            if (!invoiceStatement.poEmail?.trim()) {
                newErrors['invoiceStatement.poEmail'] = 'Email address for PO number is required';
            } else if (invoiceStatement.poEmail.trim().length > FIFTY_FIELD_MAX_LENGTH) {
                newErrors['invoiceStatement.poEmail'] = GENERAL_PO_EMAIL_LENGTH_ERROR;
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceStatement.poEmail.trim())) {
                newErrors['invoiceStatement.poEmail'] = 'Please enter a valid email address';
            }
        }
        if (!invoiceStatement.needCreditAccount) {
            newErrors['invoiceStatement.needCreditAccount'] = 'Please select an option';
        }

        return newErrors;
    }

    const validateCreditAccountDetails = () => {
        const newErrors: Record<string, string> = {};

        if (skippedCreditAccountDetails) {
            return newErrors;
        }

        if (!creditData.contactName?.trim()) {
            newErrors.contactName = 'Contact Name is required';
        }
        if (!creditData.email?.trim()) {
            newErrors.email = 'Email Address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(creditData.email.trim())) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!creditData.position?.trim()) {
            newErrors.position = 'Position is required';
        }
        if (!creditData.phoneNumber?.trim()) {
            newErrors.phoneNumber = 'Phone Number is required';
        } else if (creditData.phoneNumber.trim().length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
            newErrors.phoneNumber = 'Phone number cannot exceed 10 digits';
        }
        if (!creditData.companyWebsite?.trim()) {
            newErrors.companyWebsite = 'Company Website is required';
        }
        if (!creditData.natureOfBusiness?.trim()) {
            newErrors.natureOfBusiness = 'Nature of Business is required';
        }
        if (!creditData.dateOfIncorporation?.trim()) {
            newErrors.dateOfIncorporation = 'Date of Incorporation is required';
        }
        if (!creditData.isTrustee) {
            newErrors.isTrustee = 'Please select if applicant is a trustee';
        }
        if (!creditData.directorName?.trim()) {
            newErrors.directorName = 'Name of Director/s is required';
        }
        if (!creditData.hasCreditReferees) {
            newErrors.hasCreditReferees = 'Please select an option';
        }

        // Validate credit referees if user selected 'yes'
        if (creditData.hasCreditReferees === 'yes' && creditData.creditReferees && Array.isArray(creditData.creditReferees)) {
            creditData.creditReferees.forEach((referee: any, index: number) => {
                if (!referee.contactName?.trim()) {
                    newErrors[`referee_${referee.id}_contactName`] = `Referee ${index + 1} Contact Name is required`;
                }
                if (!referee.company?.trim()) {
                    newErrors[`referee_${referee.id}_company`] = `Referee ${index + 1} Company is required`;
                }
                if (!referee.phoneNumber?.trim()) {
                    newErrors[`referee_${referee.id}_phoneNumber`] = `Referee ${index + 1} Phone Number is required`;
                } else if (referee.phoneNumber.trim().length > CONTACT_NUMBER_FIELD_MAX_LENGTH) {
                    newErrors[`referee_${referee.id}_phoneNumber`] = `Referee ${index + 1} Phone number cannot exceed 10 digits`;
                }
                if (!referee.email?.trim()) {
                    newErrors[`referee_${referee.id}_email`] = `Referee ${index + 1} Email is required`;
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(referee.email.trim())) {
                    newErrors[`referee_${referee.id}_email`] = `Please enter a valid email address for Referee ${index + 1}`;
                }
            });
        }

        return newErrors;
    };


    const validateDeclarationForm = () => {
        const newErrors: Record<string, string> = {};
        const existingSignatureId = String(formData?.declaration?.signatureId || formData?.signatureId || '').trim();
        const hasExistingSignature = existingSignatureId.length > 0;

        if (!hasAgreedToTermsAndCondition) {
            newErrors.declaration = 'You must review and accept the terms and conditions to proceed';
        }

        if (!declarationFormData?.fullName.trim()) {
            newErrors.fullName = 'Full Name is required';
        } else if (declarationFormData.fullName.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors.fullName = FULL_NAME_LENGTH_ERROR;
        }

        if (!declarationFormData?.date.trim()) {
            newErrors.date = 'Date is required';
        }

        if (!declarationFormData?.company.trim()) {
            newErrors.company = 'Company is required';
        } else if (declarationFormData.company.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors.company = COMPANY_LENGTH_ERROR;
        }

        if (!declarationFormData?.title.trim()) {
            newErrors.title = 'Position Title is required';
        } else if (declarationFormData.title.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors.title = POSITION_LENGTH_ERROR;
        }

        if (existingSignatureId.length > LONG_TEXT_FIELD_MAX_LENGTH) {
            newErrors.signatureImage = SIGNATURE_ID_LENGTH_ERROR;
        }

        if (!signatureImage && !hasExistingSignature) {
            newErrors.signatureImage = 'Signature is required';
        }

        return newErrors;
    };

    const validateAllPages = () => {
        const generalInfoErrors = validateGeneralInfo();
        const creditAccountErrors = validateCreditAccountDetails();
        const declarationErrors = validateDeclarationForm();

        // Check General Information errors first
        if (Object.keys(generalInfoErrors).length > 0) {
            const firstError = Object.values(generalInfoErrors)[0];
            setSnackbar({
                open: true,
                message: `Please complete General Information: ${firstError}`,
                severity: 'error'
            });

            // Navigate to General Information step (step 0) with errors
            if (onGoToStep) {
                onGoToStep(0, generalInfoErrors);
            } else if (onBackToGeneralInfo) {
                onBackToGeneralInfo();
            }
            return false;
        }

        // Check Credit Account errors (only if not skipped)
        if (Object.keys(creditAccountErrors).length > 0) {
            const firstError = Object.values(creditAccountErrors)[0];
            setSnackbar({
                open: true,
                message: `Please complete Credit Account Details: ${firstError}`,
                severity: 'error'
            });

            // Navigate to Credit Account Details step (step 1) with errors
            if (onGoToStep) {
                onGoToStep(1, creditAccountErrors);
            } else {
                onBack();
            }
            return false;
        }

        // Check Declaration errors - these are on the current page
        if (Object.keys(declarationErrors).length > 0) {
            setErrors(declarationErrors);
            return false;
        }

        return true;
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    useEffect(() => {
        if (!showTermsDialog) return;

        if (hasAgreedToTermsAndCondition) {
            setCanApproveTerms(true);
            return;
        }

        setCanApproveTerms(false);
    }, [showTermsDialog, hasAgreedToTermsAndCondition]);

    useEffect(() => {
        if (showTermsDialog) {
            setCanApproveTerms(hasAgreedToTermsAndCondition);
        }
    }, [showTermsDialog, hasAgreedToTermsAndCondition]);

    const handleTermsScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (hasAgreedToTermsAndCondition) return;

        const element = event.currentTarget;
        const { scrollTop, scrollHeight, clientHeight } = element;

        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

        if (isNearBottom && !canApproveTerms) {
            setCanApproveTerms(true);
        }
    };

    const handleApproveTerms = () => {
        setHasClickedTermsLink(true);
        setHasAgreedToTermsAndCondition(true);
        setShowTermsDialog(false);
        setShowCheckbox(true);
        setCanApproveTerms(false);

        if (onFormDataChange) {
            onFormDataChange({
                declaration: {
                    ...formData?.declaration,
                    hasClickedTermsLink: true,
                    hasAgreedToTermsAndCondition: true
                }
            });
        }

        if (errors.termsLink) {
            setErrors(prev => ({ ...prev, termsLink: '' }));
        }
        if (errors.declaration) {
            setErrors(prev => ({ ...prev, declaration: '' }));
        }
    };

    const handleDownloadTerms = () => {
        const printDate = new Date().toLocaleDateString('en-AU', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
        const cmToPx = (cm: number) => (96 / 2.54) * cm;
        const pageHeightPx = cmToPx(29.7);
        const pageTopSpacerPx = 18;
        const footerHeightPx = 56;

        let htmlContent = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Terms and Condition</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { margin: 0; padding: 0; }
        body {
            font-family: Arial, Helvetica, sans-serif;
            line-height: 1.3;
            color: #000;
            background: #fff;
            font-size: 11px;
        }
        .print-root {
            position: relative;
        }
        .page-layout-table {
            width: 100%;
            border-collapse: collapse;
            border: none;
        }
        .page-layout-table thead {
            display: table-header-group;
        }
        .page-layout-table tfoot {
            display: table-footer-group;
        }
        .page-top-spacer {
            height: 18px;
            border: none;
            padding: 0;
        }
        .page-footer-spacer {
            height: 56px;
            border: none;
            padding: 0;
        }
        .page-content-cell {
            border: none;
            padding: 24px 40px 0;
            vertical-align: top;
        }
        .print-header {
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            margin-bottom: 28px;
        }
        .print-header img {
            height: 48px;
            width: auto;
            display: block;
        }
        .page-title { text-align: center; margin-bottom: 20px; }
        .page-title h1 {
            font-size: 16px;
            font-weight: bold;
            color: #003883;
            margin: 0 0 10px 0;
            text-transform: uppercase;
        }
        .title-underline {
            width: 80px;
            height: 3px;
            background-color: #003883;
            margin: 0 auto;
        }
        .section { margin-bottom: 14px; page-break-inside: avoid; }
        .section h2 {
            font-size: 12px;
            font-weight: bold;
            color: #003883;
            margin-bottom: 6px;
            border-bottom: 1px solid #003883;
            padding-bottom: 3px;
            text-transform: uppercase;
        }
        .clause {
            margin-bottom: 5px;
            line-height: 1.3;
            color: #000;
            font-size: 10px;
            text-align: justify;
        }
        .clause-id { color: #003883; font-weight: bold; margin-right: 4px; }
        .detail {
            margin-bottom: 5px;
            line-height: 1.3;
            color: #000;
            font-size: 10px;
            text-align: justify;
        }
        .print-footer-layer {
            position: absolute;
            inset: 0;
            pointer-events: none;
        }
        .print-generated-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 40px 10px;
            font-size: 10px;
            background: #fff;
            color: #333;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
        .print-generated-footer-date {
            min-width: 120px;
        }
        .print-generated-footer-page {
            min-width: 90px;
            text-align: right;
        }
        @page { size: A4; margin: 0; }
        @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .section { page-break-inside: avoid; }
        }
    </style>
</head>
<body>
    <div class="print-root" id="print-root">
        <div class="print-footer-layer" id="print-footer-layer"></div>
        <table class="page-layout-table">
            <thead><tr><td class="page-top-spacer"></td></tr></thead>
            <tfoot><tr><td class="page-footer-spacer"></td></tr></tfoot>
            <tbody><tr><td class="page-content-cell">
                <div class="print-body-content" id="print-body-content">
                    <div class="print-header">
                        <img src="${ETLogo}" alt="Eurofins Environment Testing" />
                    </div>
                    <div class="page-title">
                        <h1>${data.pageTitle}</h1>
                        <div class="title-underline"></div>
                    </div>
                    <div class="terms-content">`;

        data.sections.forEach((section) => {
            htmlContent += `<div class="section"><h2>${section.id}. ${section.title}</h2>`;
            if (section.clauses && section.clauses.length > 0) {
                section.clauses.forEach((clause) => {
                    htmlContent += `<div class="clause"><span class="clause-id">${clause.id}</span>${clause.text}</div>`;
                });
            } else if (section.details && section.details.length > 0) {
                section.details.forEach((detail) => {
                    htmlContent += `<div class="detail">${detail}</div>`;
                });
            }
            htmlContent += `</div>`;
        });

        htmlContent += `</div>
                </div>
            </td></tr></tbody>
        </table>
    </div>
</body></html>`;

        // Use a hidden iframe so no new tab opens
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc && iframe.contentWindow) {
            iframeDoc.open();
            iframeDoc.write(htmlContent);
            iframeDoc.close();
            iframeDoc.title = 'Terms and Condition';
            iframe.contentWindow.focus();
            setTimeout(() => {
                const printRoot = iframeDoc.getElementById('print-root') as HTMLElement | null;
                const footerLayer = iframeDoc.getElementById('print-footer-layer') as HTMLElement | null;
                const printBodyContent = iframeDoc.getElementById('print-body-content') as HTMLElement | null;

                if (printRoot && footerLayer && printBodyContent) {
                    const usablePageHeightPx = pageHeightPx - pageTopSpacerPx - footerHeightPx;
                    const contentHeight = Math.ceil(printBodyContent.getBoundingClientRect().height);
                    const pageCount = Math.max(1, Math.ceil(contentHeight / usablePageHeightPx));

                    printRoot.style.height = `${pageCount * pageHeightPx}px`;
                    printRoot.style.overflow = 'hidden';
                    footerLayer.style.height = `${pageCount * pageHeightPx}px`;
                    footerLayer.style.overflow = 'hidden';

                    footerLayer.innerHTML = Array.from({ length: pageCount }, (_, index) => {
                        const footerTop = ((index + 1) * pageHeightPx) - footerHeightPx;
                        return `<div class="print-generated-footer" style="position:absolute;left:0;right:0;top:${footerTop}px;height:${footerHeightPx}px;">
                            <span class="print-generated-footer-date">Date: ${printDate}</span>
                            <span class="print-generated-footer-page">Page ${index + 1}</span>
                        </div>`;
                    }).join('');
                }

                const originalTitle = document.title;
                document.title = 'Terms and Condition';
                iframe.contentWindow?.print();
                setTimeout(() => {
                    document.title = originalTitle;
                    document.body.removeChild(iframe);
                }, 1000);
            }, 300);
        }
    };

    const MAX_SIGNATURE_SIZE_BYTES = 200 * 1024;

    const generateGuid = () => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
            const randomValue = Math.random() * 16 | 0;
            const guidValue = char === 'x' ? randomValue : (randomValue & 0x3 | 0x8);
            return guidValue.toString(16);
        });
    };

    const getSignatureFileName = () => {
        const existingSignatureId = String(formData?.declaration?.signatureId || formData?.signatureId || '').trim();

        if (existingSignatureId.length > LONG_TEXT_FIELD_MAX_LENGTH) {
            throw new Error(SIGNATURE_ID_LENGTH_ERROR);
        }

        if (!existingSignatureId) {
            return `${generateGuid()}.jpeg`;
        }

        return /\.jpe?g$/i.test(existingSignatureId)
            ? existingSignatureId
            : `${existingSignatureId}.jpeg`;
    };

    const blobToDataUrl = (blob: Blob) => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to generate signature preview'));
        reader.readAsDataURL(blob);
    });

    const canvasToJpegBlob = (canvas: HTMLCanvasElement, quality: number) => new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
    });

    const createJpegSignatureFile = async (sourceCanvas: HTMLCanvasElement) => {
        let workingCanvas = document.createElement('canvas');
        workingCanvas.width = sourceCanvas.width;
        workingCanvas.height = sourceCanvas.height;

        let workingContext = workingCanvas.getContext('2d');
        if (!workingContext) {
            throw new Error('Unable to process signature image');
        }

        workingContext.fillStyle = '#fff';
        workingContext.fillRect(0, 0, workingCanvas.width, workingCanvas.height);
        workingContext.drawImage(sourceCanvas, 0, 0);

        let quality = 0.9;
        let blob = await canvasToJpegBlob(workingCanvas, quality);

        while (blob && blob.size > MAX_SIGNATURE_SIZE_BYTES && quality > 0.35) {
            quality -= 0.1;
            blob = await canvasToJpegBlob(workingCanvas, quality);
        }

        while (blob && blob.size > MAX_SIGNATURE_SIZE_BYTES && workingCanvas.width > 200) {
            const resizedCanvas = document.createElement('canvas');
            resizedCanvas.width = Math.round(workingCanvas.width * 0.9);
            resizedCanvas.height = Math.round(workingCanvas.height * 0.9);

            const resizedContext = resizedCanvas.getContext('2d');
            if (!resizedContext) {
                break;
            }

            resizedContext.fillStyle = '#fff';
            resizedContext.fillRect(0, 0, resizedCanvas.width, resizedCanvas.height);
            resizedContext.drawImage(workingCanvas, 0, 0, resizedCanvas.width, resizedCanvas.height);

            workingCanvas = resizedCanvas;
            quality = Math.min(quality, 0.8);
            blob = await canvasToJpegBlob(workingCanvas, quality);
        }

        if (!blob || blob.size > MAX_SIGNATURE_SIZE_BYTES) {
            throw new Error('Signature must be a JPEG file of 200KB or less');
        }

        const file = new File([blob], getSignatureFileName(), { type: 'image/jpeg' });
        const preview = await blobToDataUrl(blob);

        return { file, preview, canvas: workingCanvas };
    };

    const handleInputChange = (field: string, value: string) => {
        // validations
        if (field === 'company' && value.length > LONG_TEXT_FIELD_MAX_LENGTH) {
            setErrors(prev => ({
                ...prev,
                company: DECLARATION_COMPANY_LENGTH_ERROR
            }));
            return;
        }

        if (field === 'title' && value.length > TEXT_FIELD_MAX_LENGTH) {
            setErrors(prev => ({
                ...prev,
                title: DECLARATION_TITLE_LENGTH_ERROR
            }));
            return;
        }

        if (field === 'tandCComments' && value.length > LONG_TEXT_FIELD_MAX_LENGTH) {
            return;
        }

        const updatedData = {
            ...declarationFormData,
            [field]: value
        };

        // local state
        setDeclarationFormData(updatedData);

        // parent state (replace with your actual setter/callback)
        onFormDataChange({
            declaration: updatedData
        });

        // clear error
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // Signature pad functions
    const clearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
            setSignatureImage(null);
            getSignatureFromParent(null);
            if (errors.signatureImage) {
                setErrors(prev => ({ ...prev, signatureImage: '' }));
            }
        }
    };

    const captureSignatureFromCanvas = async () => {
        if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
            return null;
        }

        const canvas = signaturePadRef.current.getCanvas();
        const { file } = await createJpegSignatureFile(canvas);
        setSignatureImage(file);
        getSignatureFromParent(file);

        if (errors.signatureImage) {
            setErrors(prev => ({ ...prev, signatureImage: '' }));
        }

        return file;
    };

    const handleSignatureEnd = async () => {
        try {
            await captureSignatureFromCanvas();
        } catch (error) {
            setSnackbar({
                open: true,
                message: error instanceof Error ? error.message : 'Unable to capture signature',
                severity: 'error'
            });
        }
    };

    const getDeclarationData = () => {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        return {
            fullName: declarationFormData?.fullName,
            date: declarationFormData?.date,
            company: declarationFormData?.company,
            title: declarationFormData?.title,
            tandCComments: declarationFormData?.tandCComments,
            signature: signatureImage ? signatureImage : null,
            signatureId: formData?.declaration?.signatureId || formData?.signatureId || '',
            hasAgreedToTermsAndCondition: hasAgreedToTermsAndCondition,
            hasClickedTermsLink: hasClickedTermsLink,
            requestedDate: `${year}-${month}-${day}`
        };
    };

    const handleSubmit = () => {
        if (validateAllPages()) {
            const declarationData = getDeclarationData();
            onSubmit(declarationData);
        }
    };

    const handleSave = () => {
        if (onSave) {
            const declarationData = getDeclarationData();
            onSave(declarationData);
        }
    };

    const handleBack = () => {
        if (skippedCreditAccountDetails && onBackToGeneralInfo) {
            onBackToGeneralInfo();
        } else {
            onBack();
        }
    };

    const formatCreditReferees = (referees: any[]) => {
        if (!referees || !Array.isArray(referees) || referees.length === 0) return 'None';
        return referees.map((ref, index) =>
            `${index + 1}. ${ref.contactName || '-'} - ${ref.company || '-'} (${ref.email || '-'})`
        ).join('\n');
    };

    const toTitleCase = (str = '') =>
        str
            .toLowerCase()
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'secondary.main' }}>
                Review & Confirmation
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Please review your information below before submitting your application.
            </Typography>

            <Card sx={{ mb: 4, boxShadow: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'secondary.main' }}>
                        Application Summary
                    </Typography>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                            General Information
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, width: '30%' }}>Company Name:</TableCell>
                                        <TableCell>{generalInfo.companyName || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Trading Name:</TableCell>
                                        <TableCell>{generalInfo.tradingName || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>ABN:</TableCell>
                                        <TableCell>{generalInfo.abn || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Eurofins Contact:</TableCell>
                                        <TableCell>{generalInfo.eurofinsContactName || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Business Address:</TableCell>
                                        <TableCell>
                                            {businessAddress.street || '-'}
                                            {businessAddress.suburb && `, ${businessAddress.suburb}`}
                                            {businessAddress.state && `, ${getAustralianStateLabel(businessAddress.country, businessAddress.state)}`}
                                            {businessAddress.postcode && `, ${businessAddress.postcode}`}
                                            {businessAddress.country && `, ${businessAddress.country}`}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Postal Address:</TableCell>
                                        <TableCell>
                                            {postalAddress.street || '-'}
                                            {postalAddress.suburb && `, ${postalAddress.suburb}`}
                                            {postalAddress.state && `, ${getAustralianStateLabel(postalAddress.country, postalAddress.state)}`}
                                            {postalAddress.postcode && `, ${postalAddress.postcode}`}
                                            {postalAddress.country && `, ${postalAddress.country}`}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Contact Name:</TableCell>
                                        <TableCell>{generalInfo.contactName || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Email Address:</TableCell>
                                        <TableCell>{generalInfo.emailAddress || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Phone Number:</TableCell>
                                        <TableCell>{generalInfo.phoneNumber || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Mobile Number:</TableCell>
                                        <TableCell>{generalInfo.mobileNumber || '-'}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                            Invoice/Statement Information
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, width: '30%' }}>Contact Name for Invoices:</TableCell>
                                        <TableCell>{generalInfo.invoiceStatement?.contactName || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Email Address for Invoices:</TableCell>
                                        <TableCell>{generalInfo.invoiceStatement?.emailAddressInvoices || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Email Address for Statements:</TableCell>
                                        <TableCell>{generalInfo.invoiceStatement?.emailAddressStatements || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Phone Number:</TableCell>
                                        <TableCell>{generalInfo.invoiceStatement?.phoneNumber || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Name of Invoice Platform:</TableCell>
                                        <TableCell>{generalInfo.invoiceStatement?.invoicePlatform || '-'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Is PO Number Mandatory for Invoicing?:</TableCell>
                                        <TableCell>
                                            {generalInfo.invoiceStatement?.isPONumberMandatory
                                                ? generalInfo.invoiceStatement.isPONumberMandatory.charAt(0).toUpperCase() + generalInfo.invoiceStatement.isPONumberMandatory.slice(1)
                                                : '-'
                                            }
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Does your company/corporation use an invoice platform for submitting & delivery of invoices?:</TableCell>
                                        <TableCell>
                                            {generalInfo.invoiceStatement?.hasInvoicePlatform
                                                ? generalInfo.invoiceStatement.hasInvoicePlatform.charAt(0).toUpperCase() + generalInfo.invoiceStatement.hasInvoicePlatform.slice(1)
                                                : '-'
                                            }
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>To apply for 30 days credit account?:</TableCell>
                                        <TableCell>
                                            {generalInfo.invoiceStatement?.needCreditAccount
                                                ? generalInfo.invoiceStatement.needCreditAccount.charAt(0).toUpperCase() + generalInfo.invoiceStatement.needCreditAccount.slice(1)
                                                : '-'
                                            }
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {!skippedCreditAccountDetails && (
                        <>
                            <Divider sx={{ my: 3 }} />

                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                                    Credit Account Information
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, width: '30%' }}>Contact Name:</TableCell>
                                                <TableCell>{creditData.contactName || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Email Address:</TableCell>
                                                <TableCell>{creditData.email || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Position:</TableCell>
                                                <TableCell>{creditData.position || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Phone Number:</TableCell>
                                                <TableCell>{creditData.phoneNumber || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Company Website:</TableCell>
                                                <TableCell>{creditData.companyWebsite || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Nature of Business:</TableCell>
                                                <TableCell>{creditData.natureOfBusiness || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Date of Incorporation:</TableCell>
                                                <TableCell>{creditData.dateOfIncorporation || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Parent Company (if any):</TableCell>
                                                <TableCell>{creditData.parentCompany || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Previous Business Name/s (if any):</TableCell>
                                                <TableCell>{creditData.previousBusiness || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Maximum Credit Required:</TableCell>
                                                <TableCell>
                                                    {creditData.maxCreditRequired ? `$${creditData.maxCreditRequired}` : '-'}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Is Trustee:</TableCell>
                                                <TableCell>
                                                    {creditData.isTrustee ? 'Yes' : '-'}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Name of Directors:</TableCell>
                                                <TableCell>
                                                    <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 'inherit' }}>
                                                        {creditData.directorName || '-'}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Would you wish to provide credit referees?:</TableCell>
                                                <TableCell>
                                                    {creditData.hasCreditReferees
                                                        ? 'Yes'
                                                        : '-'}
                                                </TableCell>
                                            </TableRow>
                                            {creditData.hasCreditReferees === 'yes' && (
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>Credit Referees:</TableCell>
                                                    <TableCell>
                                                        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 'inherit' }}>
                                                            {formatCreditReferees(creditData.creditReferees)}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </>
                    )}
                </CardContent>
            </Card>

            <Card sx={{ mb: 4, boxShadow: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'secondary.main' }}>
                        Declaration <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'normal', color: 'text.secondary', textAlign: 'justify' }}>
                                I hereby authorise Eurofins to investigate the listed ABN pertinent to my credit worthiness. I agree to abide by the&nbsp;
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowTermsDialog(true);
                                    }}
                                    sx={{
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        font: 'inherit',
                                        '&:hover': {
                                            color: 'primary.dark'
                                        }
                                    }}
                                >
                                    terms and conditions
                                </Link>
                                &nbsp;of Eurofins.
                                I agree to pay Eurofins invoices within payment terms and relevant legal fees in the event of unreasonable non-compliance to payment terms.
                            </Typography>
                        </Box>
                        {errors.declaration && (
                            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                                {errors.declaration}
                            </Typography>
                        )}
                    </Box>

                    {showCheckbox && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={hasAgreedToTermsAndCondition}
                                    onChange={() => {
                                        // Checkbox is read-only - controlled by terms approval process
                                    }}
                                    color="primary"
                                    sx={{
                                        pointerEvents: 'none',
                                        '& .MuiSvgIcon-root': {
                                            color: hasAgreedToTermsAndCondition ? 'primary.main' : 'rgba(0, 0, 0, 0.26)'
                                        }
                                    }}
                                    data-testid="declaration-checkbox"
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    I have read the Eurofins | Environment Testing Terms & Conditions
                                </Typography>
                            }
                        />
                    )}
                    {errors.declaration && (
                        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                            {errors.declaration}
                        </Typography>
                    )}

                    <TextField
                        label="Comments"
                        value={declarationFormData?.tandCComments || ''}
                        onChange={(e) => handleInputChange('tandCComments', e.target.value)}
                        fullWidth
                        multiline
                        minRows={4}
                        inputProps={{ maxLength: LONG_TEXT_FIELD_MAX_LENGTH }}
                        helperText={`${declarationFormData?.tandCComments?.length}/${LONG_TEXT_FIELD_MAX_LENGTH}`}
                        size="small"
                        sx={{ mt: 2 }}
                    />

                </CardContent>
            </Card>

            <Card sx={{ mb: 4, boxShadow: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'secondary.main' }}>
                        Signature & Authorization
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Input
                                label="Full Name"
                                value={declarationFormData?.fullName}
                                onChange={(e) => handleInputChange('fullName', e.target.value)}
                                onBlur={(e) => handleInputChange('fullName', toTitleCase(e.target.value))}
                                required
                                fullWidth
                                error={!!errors.fullName}
                                helperText={errors.fullName}
                                data-testid="full-name-input"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Input
                                label="Date"
                                type="date"
                                value={declarationFormData?.date}
                                onChange={(e) => handleInputChange('date', e.target.value)}
                                required
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.date}
                                helperText={errors.date}
                                data-testid="date-input"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Input
                                label="Company"
                                value={declarationFormData?.company}
                                onChange={(e) => handleInputChange('company', e.target.value)}
                                required
                                fullWidth
                                error={!!errors.company}
                                helperText={errors.company}
                                data-testid="company-input"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Input
                                label="Position Title"
                                value={declarationFormData?.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                onBlur={(e) => handleInputChange('title', toTitleCase(e.target.value))}
                                required
                                fullWidth
                                error={!!errors.title}
                                helperText={errors.title}
                                data-testid="title-input"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Signature <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                                Draw your signature using your mouse or touchpad
                            </Typography>
                            <Box
                                sx={{
                                    border: errors.signatureImage ? '2px solid #d32f2f' : '2px solid #ccc',
                                    borderRadius: 2,
                                    backgroundColor: '#fff',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                            >
                                <SignatureCanvas
                                    ref={signaturePadRef}
                                    onEnd={handleSignatureEnd}
                                    minWidth={0.5}
                                    maxWidth={1.5}
                                    canvasProps={{
                                        width: 600,
                                        height: 200,
                                        className: 'signature-canvas',
                                        style: {
                                            width: '100%',
                                            height: '200px',
                                        }
                                    }}
                                    backgroundColor="#fff"
                                    penColor="#000"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ClearIcon />}
                                    onClick={clearSignature}
                                >
                                    Clear
                                </Button>
                            </Box>
                            {errors.signatureImage && (
                                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                                    {errors.signatureImage}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {!hasAgreedToTermsAndCondition && (
                <Alert severity="warning" sx={{ mb: 2, alignItems: 'center' }}>
                    Please review and accept the terms and conditions before submitting.
                </Alert>
            )}

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 4,
                    flexDirection: {
                        xs: 'column',
                        sm: 'row',
                    },
                    gap: 2,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={handleBack}
                    size="medium"
                    startIcon={<ArrowBackIcon />}
                    sx={{
                        fontWeight: 600,
                        textTransform: 'none',
                        width: {
                            xs: '100%',
                            sm: 'auto',
                        },
                        minWidth: {
                            sm: 110,
                        },
                    }}
                    data-testid="back-button"
                >
                    Back
                </Button>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: {
                            xs: 'column',
                            sm: 'row',
                        },
                        width: {
                            xs: '100%',
                            sm: 'auto',
                        },
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleSave}
                        disabled={isSaving || isDisabled}
                        size="medium"
                        startIcon={<SaveIcon />}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            width: {
                                xs: '100%',
                                sm: 'auto',
                            },
                            minWidth: {
                                sm: 110,
                            },
                        }}
                        data-testid="save-button"
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>

                    <Tooltip
                        title={
                            !hasAgreedToTermsAndCondition
                                ? "Please review and accept the terms and conditions before submitting"
                                : ""
                        }
                        arrow
                    >
                        <span>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={
                                    isSubmitting ||
                                    isDisabled ||
                                    !hasAgreedToTermsAndCondition
                                }
                                size="medium"
                                startIcon={<SendIcon />}
                                sx={{
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    width: {
                                        xs: '100%',
                                        sm: 'auto',
                                    },
                                    minWidth: {
                                        sm: 190,
                                    },
                                    backgroundColor: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'grey.300',
                                        color: 'grey.500',
                                    },
                                }}
                                data-testid="submit-button"
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit Application'}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Terms and Conditions Dialog */}
            <Dialog
                open={showTermsDialog}
                onClose={() => setShowTermsDialog(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { height: '90vh', maxHeight: '90vh' }
                }}
            >
                <DialogTitle sx={{
                    fontWeight: 600,
                    fontSize: '1.2rem',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ fontSize: '16px' }}>
                        Terms & Conditions - Eurofins Environment Testing
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={handleDownloadTerms}
                            sx={{
                                color: 'primary.main',
                                border: '1px solid #ee7d11',
                                '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'white'
                                }
                            }}
                            title="Download Terms & Conditions as PDF"
                        >
                            <DownloadIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={{ p: 0 }}
                    onScroll={handleTermsScroll}
                >
                    <Box sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <TermsAndConditions />
                    </Box>
                </DialogContent>
                <DialogActions sx={{
                    p: 3,
                    borderTop: '1px solid #e0e0e0',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box sx={{ flex: 1, minWidth: '260px' }}>
                        {!canApproveTerms && !hasAgreedToTermsAndCondition && (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: '14px',
                                    backgroundColor: '#fff7ed',
                                    border: '1px solid #fed7aa',
                                    boxShadow: '0 4px 14px rgba(238, 125, 17, 0.08)'
                                }}
                            >
                                <InfoOutlinedIcon sx={{ color: '#ee7d11', fontSize: 18 }} />
                                <Typography variant="body2" sx={{ color: '#7a3e00', fontWeight: 600, lineHeight: 1.4 }}>
                                    Please scroll down to review and approve the Terms and Conditions of Sale.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            flexDirection: {
                                xs: 'column',
                                sm: 'row',
                            },
                            width: '100%',
                        }}
                    >
                        <Button
                            onClick={() => setShowTermsDialog(false)}
                            variant="outlined"
                            sx={{
                                fontWeight: 600,
                                textTransform: 'none',
                                width: {
                                    xs: '100%',
                                    sm: 'auto',
                                },
                                minWidth: {
                                    sm: 100,
                                },
                            }}
                        >
                            Close
                        </Button>

                        <Button
                            onClick={handleApproveTerms}
                            variant="contained"
                            color="primary"
                            disabled={!canApproveTerms}
                            sx={{
                                fontWeight: 600,
                                textTransform: 'none',
                                width: {
                                    xs: '100%',
                                    sm: 'auto',
                                },
                                minWidth: {
                                    sm: 200,
                                },
                                backgroundColor: 'primary.main',
                                '&:hover': {
                                    backgroundColor: 'primary.dark',
                                },
                                '&:disabled': {
                                    backgroundColor: 'grey.300',
                                    color: 'grey.500',
                                },
                            }}
                            title={
                                !canApproveTerms && !hasAgreedToTermsAndCondition
                                    ? "Please scroll to the bottom of the terms and conditions to enable this button"
                                    : ""
                            }
                        >
                            Approve & Accept Terms
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
